import type { Board } from "../../board";
import type { DamageType } from "../../damageType";
import type { Direction } from "../../direction";
import { emitBloodEffect } from "../../emitBloodEffect";
import { isBumpable, type PostDeserializable } from "../../interfaces";
import type { PhysicalThing } from "../../physicalThing";
import { PhysicalThingWithHp } from "../../physicalThingWithHp";
import { deserializeXY, serializeXY } from "../../serialization";
import type { Thing } from "../../thing";
import { XY } from "../../xy";
import { MultiTileThingController } from "./multiTileThingController";
import { MultiTileThingCore } from "./multiTileThingCore";

export class MultiTileThingPart extends PhysicalThingWithHp implements PostDeserializable {
	static key = "MultiTileThingPart";
	controller!: MultiTileThingController;
	private _pendingCoreXY: { x: number; y: number } | null = null;

	constructor(xy: XY) {
		super(xy, false);
	}

	linkToController(controller: MultiTileThingController): void {
		this.controller = controller;
		this.blocksMovement = controller.getThing().blocksMovement;

		const core = controller.getThing();
		Object.defineProperty(this, "hp", {
			get() {
				return core.hp;
			},
			set(v: number) {
				core.hp = v;
			},
			configurable: true,
			enumerable: true,
		});
		Object.defineProperty(this, "maxHp", {
			get() {
				return core.maxHp;
			},
			set(v: number) {
				core.maxHp = v;
			},
			configurable: true,
			enumerable: true,
		});
		Object.defineProperty(this, "visualId", {
			get() {
				return core.visualId;
			},
			configurable: true,
			enumerable: true,
		});
	}

	override getName(): string {
		return this.controller.getThing().getName();
	}

	override getNameKey(): string {
		return this.controller.getThing().getNameKey();
	}

	override getZOrder(): number {
		return this.controller.getThing().getZOrder();
	}

	override isVisible(): boolean {
		return this.controller.getThing().isVisible();
	}

	override isHideUnderneath(): boolean {
		return this.controller.getThing().isHideUnderneath();
	}

	override applyDamage(type: DamageType | null, amount: number, source: Thing | null): number {
		const core = this.controller.getThing();
		if (!core.active) return 0;
		const dmg = core.applyDamage(type, amount, source);
		const bloodType = core.getBloodParticleType();
		if (dmg > 0 && bloodType !== null && this.board) {
			emitBloodEffect(this.board, this.xy, bloodType, type, source);
		}
		return dmg;
	}

	isHittable(): boolean {
		return true;
	}

	onProjectileHit(projectile: unknown): void {
		this.controller.onProjectileHit(projectile);
	}

	bump(direction: Direction, author: PhysicalThing): boolean {
		const core = this.controller.getThing();
		if (isBumpable(core)) {
			return core.bump(direction, author);
		}
		return false;
	}

	postDeserialize(board: Board): void {
		this.relinkUsingThings(board.getAllThings());
	}

	relinkUsingThings(things: Iterable<Thing>): void {
		if (!this._pendingCoreXY) return;

		const coreXY = new XY(this._pendingCoreXY.x, this._pendingCoreXY.y);
		let core: MultiTileThingCore | undefined;
		for (const t of things) {
			if (t instanceof MultiTileThingCore && t.xy.equals(coreXY)) {
				core = t;
				break;
			}
		}

		if (core) {
			if (!core.controller) {
				core.controller = new MultiTileThingController(core, new Set());
			}
			core.controller.getParts().add(this);
			this.linkToController(core.controller);
		}

		this._pendingCoreXY = null;
	}

	serialize(): Record<string, unknown> {
		return {
			type: (this.constructor as any).key,
			xy: serializeXY(this.xy),
			coreXY: serializeXY(this.controller.getThing().xy),
		};
	}

	static deserialize(data: any): MultiTileThingPart {
		const part = new MultiTileThingPart(deserializeXY(data.xy));
		part._pendingCoreXY = data.coreXY;
		return part;
	}
}
